/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Tessera Backend API
 * Tessera backend application with Bearer token authentication and multi-tenant support
 */
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  InvalidateOptions,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { ErrorType } from '../api-instance';
import { apiInstance } from '../api-instance';
import type {
  ListWorklistTasksParams,
  TLHTTPValidationError,
  TLPaginatedResponseWorkItem,
  TLSuccessResponse,
  TLWorkItemStatus,
} from './tessera-backend-api.schemas';

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * @summary Trigger Worklist Backfill
 */
export const triggerWorklistBackfill = (options?: SecondParameter<typeof apiInstance>, signal?: AbortSignal) => {
  return apiInstance<TLSuccessResponse>({ url: `/worklist/admin/backfill`, method: 'POST', signal }, options);
};

export const getTriggerWorklistBackfillQueryKey = () => {
  return ['POST', 'worklist', 'admin', 'backfill'] as const;
};

export const getTriggerWorklistBackfillQueryOptions = <
  TData = Awaited<ReturnType<typeof triggerWorklistBackfill>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof triggerWorklistBackfill>>, TError, TData>>;
  request?: SecondParameter<typeof apiInstance>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getTriggerWorklistBackfillQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof triggerWorklistBackfill>>> = ({ signal }) =>
    triggerWorklistBackfill(requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof triggerWorklistBackfill>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type TriggerWorklistBackfillQueryResult = NonNullable<Awaited<ReturnType<typeof triggerWorklistBackfill>>>;
export type TriggerWorklistBackfillQueryError = ErrorType<unknown>;

export function useTriggerWorklistBackfill<
  TData = Awaited<ReturnType<typeof triggerWorklistBackfill>>,
  TError = ErrorType<unknown>,
>(
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof triggerWorklistBackfill>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof triggerWorklistBackfill>>,
          TError,
          Awaited<ReturnType<typeof triggerWorklistBackfill>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useTriggerWorklistBackfill<
  TData = Awaited<ReturnType<typeof triggerWorklistBackfill>>,
  TError = ErrorType<unknown>,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof triggerWorklistBackfill>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof triggerWorklistBackfill>>,
          TError,
          Awaited<ReturnType<typeof triggerWorklistBackfill>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useTriggerWorklistBackfill<
  TData = Awaited<ReturnType<typeof triggerWorklistBackfill>>,
  TError = ErrorType<unknown>,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof triggerWorklistBackfill>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Trigger Worklist Backfill
 */

export function useTriggerWorklistBackfill<
  TData = Awaited<ReturnType<typeof triggerWorklistBackfill>>,
  TError = ErrorType<unknown>,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof triggerWorklistBackfill>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getTriggerWorklistBackfillQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Trigger Worklist Backfill
 */
export const prefetchTriggerWorklistBackfillQuery = async <
  TData = Awaited<ReturnType<typeof triggerWorklistBackfill>>,
  TError = ErrorType<unknown>,
>(
  queryClient: QueryClient,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof triggerWorklistBackfill>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getTriggerWorklistBackfillQueryOptions(options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Trigger Worklist Backfill
 */
export const invalidateTriggerWorklistBackfill = async (
  queryClient: QueryClient,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getTriggerWorklistBackfillQueryKey() }, options);

  return queryClient;
};

/**
 * Returns a paginated list of worklist tasks.
 * @summary List worklist tasks
 */
export const listWorklistTasks = (
  params?: ListWorklistTasksParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLPaginatedResponseWorkItem>({ url: `/worklist/tasks`, method: 'GET', params, signal }, options);
};

export const getListWorklistTasksQueryKey = (params?: ListWorklistTasksParams) => {
  return ['worklist', 'tasks', ...(params ? [params] : [])] as const;
};

export const getListWorklistTasksQueryOptions = <
  TData = Awaited<ReturnType<typeof listWorklistTasks>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListWorklistTasksParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listWorklistTasks>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListWorklistTasksQueryKey(params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listWorklistTasks>>> = ({ signal }) =>
    listWorklistTasks(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof listWorklistTasks>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListWorklistTasksQueryResult = NonNullable<Awaited<ReturnType<typeof listWorklistTasks>>>;
export type ListWorklistTasksQueryError = ErrorType<TLHTTPValidationError>;

export function useListWorklistTasks<
  TData = Awaited<ReturnType<typeof listWorklistTasks>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params: undefined | ListWorklistTasksParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof listWorklistTasks>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listWorklistTasks>>,
          TError,
          Awaited<ReturnType<typeof listWorklistTasks>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListWorklistTasks<
  TData = Awaited<ReturnType<typeof listWorklistTasks>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListWorklistTasksParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listWorklistTasks>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listWorklistTasks>>,
          TError,
          Awaited<ReturnType<typeof listWorklistTasks>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListWorklistTasks<
  TData = Awaited<ReturnType<typeof listWorklistTasks>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListWorklistTasksParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listWorklistTasks>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List worklist tasks
 */

export function useListWorklistTasks<
  TData = Awaited<ReturnType<typeof listWorklistTasks>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  params?: ListWorklistTasksParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listWorklistTasks>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListWorklistTasksQueryOptions(params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary List worklist tasks
 */
export const prefetchListWorklistTasksQuery = async <
  TData = Awaited<ReturnType<typeof listWorklistTasks>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  params?: ListWorklistTasksParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listWorklistTasks>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getListWorklistTasksQueryOptions(params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary List worklist tasks
 */
export const invalidateListWorklistTasks = async (
  queryClient: QueryClient,
  params?: ListWorklistTasksParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getListWorklistTasksQueryKey(params) }, options);

  return queryClient;
};

/**
 * Returns task counts grouped by status. All WorkItemStatus keys are always present, zero-filled when no items exist. Non-admins are scoped to their own tasks; admins receive global counts.
 * @summary Get task counts by status
 */
export const getTaskCountsByStatus = (options?: SecondParameter<typeof apiInstance>, signal?: AbortSignal) => {
  return apiInstance<Partial<Record<TLWorkItemStatus, number>>>(
    { url: `/worklist/tasks/stats`, method: 'GET', signal },
    options,
  );
};

export const getGetTaskCountsByStatusQueryKey = () => {
  return ['worklist', 'tasks', 'stats'] as const;
};

export const getGetTaskCountsByStatusQueryOptions = <
  TData = Awaited<ReturnType<typeof getTaskCountsByStatus>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getTaskCountsByStatus>>, TError, TData>>;
  request?: SecondParameter<typeof apiInstance>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetTaskCountsByStatusQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getTaskCountsByStatus>>> = ({ signal }) =>
    getTaskCountsByStatus(requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getTaskCountsByStatus>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetTaskCountsByStatusQueryResult = NonNullable<Awaited<ReturnType<typeof getTaskCountsByStatus>>>;
export type GetTaskCountsByStatusQueryError = ErrorType<unknown>;

export function useGetTaskCountsByStatus<
  TData = Awaited<ReturnType<typeof getTaskCountsByStatus>>,
  TError = ErrorType<unknown>,
>(
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getTaskCountsByStatus>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getTaskCountsByStatus>>,
          TError,
          Awaited<ReturnType<typeof getTaskCountsByStatus>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetTaskCountsByStatus<
  TData = Awaited<ReturnType<typeof getTaskCountsByStatus>>,
  TError = ErrorType<unknown>,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getTaskCountsByStatus>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getTaskCountsByStatus>>,
          TError,
          Awaited<ReturnType<typeof getTaskCountsByStatus>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetTaskCountsByStatus<
  TData = Awaited<ReturnType<typeof getTaskCountsByStatus>>,
  TError = ErrorType<unknown>,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getTaskCountsByStatus>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get task counts by status
 */

export function useGetTaskCountsByStatus<
  TData = Awaited<ReturnType<typeof getTaskCountsByStatus>>,
  TError = ErrorType<unknown>,
>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getTaskCountsByStatus>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetTaskCountsByStatusQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get task counts by status
 */
export const prefetchGetTaskCountsByStatusQuery = async <
  TData = Awaited<ReturnType<typeof getTaskCountsByStatus>>,
  TError = ErrorType<unknown>,
>(
  queryClient: QueryClient,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getTaskCountsByStatus>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetTaskCountsByStatusQueryOptions(options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get task counts by status
 */
export const invalidateGetTaskCountsByStatus = async (
  queryClient: QueryClient,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetTaskCountsByStatusQueryKey() }, options);

  return queryClient;
};
